import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/es";
import "dayjs/locale/en";

export function addClasses(...args: string[]): string {
  return args.filter((item) => !!item).join(" ");
}

/**
 * Finds if any element of targetArray is inside sourceArray
 *
 * @param {Array<string>} sourceArray - Array of strings
 * @param {Array<string>} targetArray - Array of strings
 * @param {boolean} [all=false]
 * @returns {boolean}
 */
export function listIncludes<T>(sourceArray: T[], targetArray: T[], all: boolean = false): boolean {
  if (!targetArray) {
    console.warn("list includes : empty target");
    return false;
  }
  if (all) {
    return targetArray.every((item) => sourceArray.includes(item));
  }
  return targetArray.some((item) => sourceArray.includes(item));
}

export function hasAnyItems<T>(sourceArray: T[], ...items: T[]): boolean {
  if (!items) {
    console.warn("has any items : empty target");
    return false;
  }
  return items.some((element) => sourceArray.includes(element));
}

export function oneOf<T>(item: T, list: T | T[], ...rest: T[]): boolean {
  if (!list) {
    console.warn("oneOf : empty list");
    return false;
  }
  if (Array.isArray(list)) {
    return list.includes(item);
  }
  const items = [list, ...rest];
  return items.includes(item);
}

export function hasAction(campaign: any, type: string): any {
  return campaign?.actions?.find((action: any) => action.type === type);
}

export function isAdmin(user: { profile?: string } | null | undefined): boolean {
  return user?.profile === "ADMIN";
}

export function isBooster(user: { profile?: string } | null | undefined): boolean {
  return user?.profile === "BOOSTER" && !window.premium;
}

/**
 * Parse CSV text into rows of cells, honoring RFC-4180 quoted fields:
 * quotes protect embedded separators, newlines, and escaped quotes ("").
 * A plain `text.split("\n")` breaks on headers/values that contain newlines
 * inside quotes (e.g. a "sms\nProspection" column name) — this doesn't.
 */
export function parseCSV(text: string, separator: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;

  const endCell = () => { row.push(cell); cell = ""; };
  const endRow = () => { endCell(); rows.push(row); row = []; };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted && c === '"' && text[i + 1] === '"') { cell += '"'; i++; } // escaped quote
    else if (c === '"') quoted = !quoted;
    else if (quoted) cell += c;
    else if (c === separator) endCell();
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++; // CRLF
      endRow();
    } else cell += c;
  }
  if (cell.length || row.length) endRow();
  return rows.filter((r) => r.some((v) => v.trim().length)); // drop blank lines
}

/**
 * Extract the values of one column from a CSV file.
 * `columnSpec` is the column name with the separator appended as its last
 * char (as produced by the column picker), e.g. "Tel client,".
 */
// Strips wrapping quotes left over from a column name saved before quote-aware parsing existed.
function normalizeHeader(value: string): string {
  return value.trim().replace(/^"(.*)"$/, "$1").trim();
}

export function extractCSVColumn(text: string, columnSpec: string): string[] {
  const separator = columnSpec[columnSpec.length - 1];
  const name = normalizeHeader(columnSpec.slice(0, -1));
  const rows = parseCSV(text, separator);
  if (!rows.length) throw new Error("Empty CSV");
  const col = rows[0].findIndex((h) => normalizeHeader(h) === name);
  if (col === -1) throw new Error(`Column "${name}" not found in CSV`);
  return rows.slice(1).map((r) => (r[col] ?? "").trim()).filter(Boolean);
}

const SEPARATORS = [",", ";", "\t"];

/** Picks whichever separator splits the header row into the most columns. */
export function detectSeparator(row: string): string {
  return SEPARATORS.reduce((best, sep) => (row.split(sep).length > row.split(best).length ? sep : best), ",");
}

const PHONE_PATTERN = /^\+?[0-9()\-.\s]{6,20}$/;

export function isPhoneLike(value: string): boolean {
  return PHONE_PATTERN.test(value.trim());
}

/** Share of non-empty values that look like phone numbers — used to warn when the wrong column was picked. */
export function phoneColumnMatchRatio(values: string[]): number {
  const nonEmpty = values.map((v) => v.trim()).filter(Boolean);
  if (!nonEmpty.length) return 1;
  return nonEmpty.filter(isPhoneLike).length / nonEmpty.length;
}

export function csvLength(file: File | null | undefined, setter: (count: number) => void): void {
  if (file) {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        setter(result.split("\n").length - 2);
      }
    };
    reader.readAsText(file);
  } else {
    setter(0);
  }
}

/** "19 Août 2026" for the given locale. Both players tables format `last` here. */
export function formatDateByLocale(dateString: string, locale: string): string {
  if (!dateString) return "";
  const date = dayjs(dateString).locale(locale.toLowerCase());
  if (!date.isValid()) return dateString;
  const month = date.format("MMMM");
  return `${date.format("D")} ${month.charAt(0).toUpperCase() + month.slice(1)} ${date.format("YYYY")}`;
}
